import { useEffect, useState } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import PageMeta from "@/components/PageMeta";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import { useImportGuides } from "@/lib/importGuides";
import {
  ForkKnife, CheckCircle, ArrowRight, Sparkle, Globe, Camera, FileText,
} from "@phosphor-icons/react";

// SEO landing-page content — one page per import source. Each entry gives us:
//  - the H1 + hero copy tuned to the query intent
//  - a 4-step HowTo suitable for JSON-LD schema
//  - the visible "import" call-to-action (file / URL / screenshot)
//
// The routes are wired in App.js as /:slug for every FORMAT_META slug plus
// three "rescue-from-*" slugs. This one component renders them all.
// Landing slug → step-by-step guide key (/help/import/:key).
const GUIDE_FOR = {
  ...Object.fromEntries(["paprika", "mela", "recipe-keeper", "cookmate", "copy-me-that", "bigoven", "whisk", "anylist", "plan-to-eat",
    "zestio", "notion", "evernote", "apple-notes", "google-keep", "obsidian", "mealmaster", "bookmarks", "url"].map((k) => [`import-from-${k}`, k])),
  ...Object.fromEntries(["clove", "osta", "recime", "pepperplate", "living-cookbook", "macgourmet"].map((k) => [`rescue-from-${k}`, "screenshot"])),
};


const LANDING = {
  // -------- Dedicated apps --------
  "import-from-paprika": {
    label: "Paprika",
    method: "file",
    accept: ".paprikarecipes,.paprikarecipe",
    tagline: "Rebuild your Paprika shelf in RecipeFiler in under a minute — photos, notes, categories and all.",
    keywords: "import paprika recipes, migrate paprika to recipefiler, paprika alternative, .paprikarecipes",
    export_steps: [
      "Open Paprika on Mac / Windows / iOS.",
      "Select every recipe (Cmd/Ctrl+A) or a category.",
      "File → Export → Paprika Recipe Format → save the .paprikarecipes file.",
    ],
  },
  "import-from-mela": {
    label: "Mela",
    method: "file",
    accept: ".melarecipes,.melarecipe",
    tagline: "Move every Mela recipe over — inline photos, sections and source links preserved.",
    keywords: "import mela recipes, migrate mela to recipefiler, .melarecipes file, mela alternative",
    export_steps: [
      "Open Mela on your Mac / iPad / iPhone.",
      "Tap the ⋯ menu → Export.",
      "Choose Mela Recipe (.melarecipes) and share it to Files.",
    ],
  },
  "import-from-recipe-keeper": {
    label: "Recipe Keeper",
    method: "file",
    accept: ".zip",
    tagline: "Import your entire Recipe Keeper backup zip in one click.",
    keywords: "import recipe keeper, migrate recipe keeper, recipe keeper backup, recipe keeper alternative",
    export_steps: [
      "In Recipe Keeper: Settings → Backup & Restore.",
      "Tap Backup to file — this creates a recipes-YYYY-MM-DD.zip.",
      "AirDrop or share the .zip to your computer.",
    ],
  },
  "import-from-cookmate": {
    label: "Cookmate",
    method: "file",
    accept: ".mcb",
    tagline: "Bring your Cookmate (formerly My CookBook) library into RecipeFiler.",
    keywords: "import cookmate, migrate my cookbook, .mcb file, cookmate alternative",
    export_steps: [
      "Open Cookmate on Android or iOS.",
      "Menu → Import / Export → Export to file.",
      "Choose Cookmate format (.mcb) and share it out.",
    ],
  },
  "import-from-copy-me-that": {
    label: "Copy Me That",
    method: "file",
    accept: ".csv,.json,.zip",
    tagline: "Bring every Copy Me That recipe over — full ingredients, directions, and source URLs.",
    keywords: "import copy me that, copymethat migration, copymethat alternative, .copymethat file",
    export_steps: [
      "Open Copy Me That in a browser.",
      "Account → Export → Export as CSV (or JSON).",
      "Download the file to your computer.",
    ],
  },
  "import-from-bigoven": {
    label: "BigOven",
    method: "file",
    accept: ".bxr,.csv,.json,.zip",
    tagline: "Move your BigOven library — .bxr, CSV, JSON all supported.",
    keywords: "import bigoven, migrate from bigoven, .bxr file, bigoven alternative",
    export_steps: [
      "Log in to BigOven on the web.",
      "Profile → Settings → Export My Recipes.",
      "Download the .bxr (or CSV) archive.",
    ],
  },
  "import-from-notion": {
    label: "Notion",
    method: "file",
    accept: ".csv,.json",
    tagline: "Turn your Notion recipe database into a beautiful, print-ready cookbook.",
    keywords: "import notion recipes, notion recipe database migration, csv notion recipes",
    export_steps: [
      "Open your Notion recipe database.",
      "⋯ menu → Export → Format: CSV (or JSON).",
      "Choose \"Include databases: Everything.\"",
    ],
  },
  "import-from-evernote": {
    label: "Evernote",
    method: "file",
    accept: ".enex",
    tagline: "Rescue every recipe note stuck in Evernote — .enex export supported natively.",
    keywords: "import evernote recipes, enex to recipefiler, evernote recipe alternative",
    export_steps: [
      "Open Evernote → find your Recipes notebook (or select notes).",
      "Right-click → Export → Evernote format (.enex).",
      "Save the .enex file.",
    ],
  },
  "import-from-mealmaster": {
    label: "MealMaster",
    method: "file",
    accept: ".mmf,.mm,.txt",
    tagline: "Bring your family's MealMaster .mmf files into the modern era.",
    keywords: "import mealmaster, mmf file, meal master recipes, mmf converter",
    export_steps: [
      "Locate your .mmf file (or a folder of them).",
      "If they're separate, concatenate into one .mmf.",
      "We'll parse every \"Recipe via Meal-Master\" block automatically.",
      "Original text is preserved for anything that doesn't map cleanly.",
    ],
  },
  "import-from-apple-notes": {
    label: "Apple Notes",
    method: "file",
    accept: ".txt",
    tagline: "Turn plain-text Apple Notes recipes into structured, editable RecipeFiler recipes.",
    keywords: "import apple notes recipes, apple notes to recipefiler, apple notes recipe alternative",
    export_steps: [
      "Open Notes on Mac → File → Export as PDF is no use to us; instead:",
      "Copy the note text (or a selection of notes separated by ------).",
      "Paste into a .txt file and save.",
      "We'll auto-detect the Ingredients + Method sections.",
    ],
  },
  "import-from-bookmarks": {
    label: "browser bookmarks",
    method: "file",
    accept: ".html",
    tagline: "Turn your Chrome / Safari / Firefox recipes folder into a real cookbook.",
    keywords: "import browser bookmarks recipes, bookmarks.html recipes, chrome bookmarks to cookbook",
    export_steps: [
      "Chrome: Bookmarks manager → ⋯ menu → Export bookmarks.",
      "Safari: File → Export → Bookmarks.",
      "Firefox: Bookmarks → Show all → Import & Backup → Export to HTML.",
      "We'll fetch every recipe URL under a \"Recipes\" folder.",
    ],
  },
  "import-from-url": {
    label: "the web",
    method: "url",
    tagline: "Paste any recipe URL — NYT Cooking, BBC Good Food, Serious Eats, personal blogs — and we'll rehost the photo so it never breaks.",
    keywords: "import recipe from url, save recipe from website, recipe URL importer",
    export_steps: [
      "Open the recipe page you want to save.",
      "Copy the full URL.",
      "Paste it in on the /try page (or up to 500 URLs on /import).",
      "We'll fetch schema.org data, rehost the hero, and give you a fully editable recipe.",
    ],
  },
  // -------- Rescue landings --------
  "rescue-from-clove": {
    label: "Clove",
    method: "screenshot",
    tagline: "Leaving Clove? Bring every recipe with you — screenshot each one and our OCR rebuilds it.",
    keywords: "clove app alternative, clove recipe alternative, move from clove, clove import",
    export_steps: [
      "Open each recipe in Clove.",
      "Screenshot the full recipe page (Shift-Cmd-4 or iPhone screenshot).",
      "Our OCR + AI turns each screenshot into a full editable recipe.",
    ],
  },
  "rescue-from-osta": {
    label: "Osta",
    method: "screenshot",
    tagline: "Moving on from Osta? Take every recipe with you with a quick screenshot and our AI structuring pipeline.",
    keywords: "osta alternative, move from osta, osta recipe alternative",
    export_steps: [
      "Open each recipe in Osta.",
      "Screenshot the full recipe page.",
      "OCR + AI rebuild it as a fully editable recipe.",
    ],
  },
  "rescue-from-recime": {
    label: "Recime",
    method: "screenshot",
    tagline: "Recime users — bring every saved recipe over to RecipeFiler with screenshots.",
    keywords: "recime shutting down, recime alternative, rescue recime recipes, recime import",
    export_steps: [
      "Open each recipe in Recime.",
      "Screenshot the full recipe page.",
      "Instant structured recipe.",
    ],
  },
  "rescue-from-pepperplate": {
    label: "Pepperplate",
    method: "screenshot",
    tagline: "Pepperplate shut down in 2022 — rescue your recipes with screenshots or paste-text.",
    keywords: "pepperplate shut down, pepperplate alternative, rescue pepperplate recipes",
    export_steps: [
      "If you still have Pepperplate open somewhere, screenshot each recipe.",
      "If you only have your notes / printouts, snap a photo of those.",
      "Drop the images on our /scan page — one recipe per scan is fine.",
      "Our OCR + AI rebuilds each one as a fully editable recipe.",
    ],
  },
  "rescue-from-living-cookbook": {
    label: "Living Cookbook",
    method: "screenshot",
    tagline: "Living Cookbook was sunset in 2020. Rescue your archive into a modern shareable cookbook.",
    keywords: "living cookbook sunset, living cookbook alternative, .lco file, rescue living cookbook",
    export_steps: [
      "Open Living Cookbook on your Windows machine (if you still have it running).",
      "For each recipe: File → Print Preview → screenshot the preview.",
      "OCR + AI restore each recipe with ingredients, method and photo.",
    ],
  },
  "rescue-from-macgourmet": {
    label: "MacGourmet",
    method: "screenshot",
    tagline: "MacGourmet is no longer maintained — rescue every recipe you love into RecipeFiler.",
    keywords: "macgourmet alternative, macgourmet sunset, rescue macgourmet recipes",
    export_steps: [
      "Open MacGourmet on your Mac (Mavericks/High Sierra recommended).",
      "Screenshot each recipe (Shift-Cmd-4).",
      "Or export to plain text and drop that on /import — either works.",
    ],
  },
  // -------- New app parsers (Sprint 2b) --------
  "import-from-whisk": {
    label: "Whisk / Samsung Food",
    method: "file",
    accept: ".csv,.json,.zip",
    tagline: "Bring your Whisk (now Samsung Food) recipe box over — full ingredients, servings and photos.",
    keywords: "import whisk recipes, samsung food alternative, samsung food export, whisk to recipefiler",
    export_steps: [
      "Log in to Samsung Food (samsungfood.com) or Whisk.",
      "Account → Settings → Export My Data.",
      "Download the CSV or JSON archive.",
    ],
  },
  "import-from-anylist": {
    label: "AnyList",
    method: "file",
    accept: ".json,.anylist",
    tagline: "Move every AnyList recipe over — ingredient dicts, preparation steps, source URLs preserved.",
    keywords: "import anylist recipes, anylist export, anylist alternative",
    export_steps: [
      "In AnyList on Mac / iPad → Settings → Export.",
      "Choose \"Export Recipes as JSON\".",
      "Save the .json (or .anylist) file.",
    ],
  },
  "import-from-plan-to-eat": {
    label: "Plan to Eat",
    method: "file",
    accept: ".csv",
    tagline: "Bring your Plan to Eat recipe book over — categories, source URLs and photos preserved.",
    keywords: "import plan to eat, plantoeat export, plan to eat alternative",
    export_steps: [
      "Log in to Plan to Eat on the web.",
      "Account → Recipe Book → Export → CSV.",
      "Download the .csv file.",
    ],
  },
  "import-from-zestio": {
    label: "Zestio",
    method: "file",
    accept: ".json,.zip",
    tagline: "Bring your Zestio recipes into a print-ready RecipeFiler shelf.",
    keywords: "import zestio recipes, zestio export, zestio alternative",
    export_steps: [
      "Open Zestio and go to Settings → Export.",
      "Save the JSON file (or zip archive with photos).",
      "Everything comes across — ingredients, steps, photos, source URLs.",
    ],
  },
  // -------- Notes / paste-text friendly --------
  "import-from-google-keep": {
    label: "Google Keep",
    method: "file",
    accept: ".txt,.md",
    tagline: "Turn recipes trapped in Google Keep into structured, editable RecipeFiler recipes.",
    keywords: "import google keep recipes, google keep to recipefiler, google keep recipe alternative",
    export_steps: [
      "Go to Google Takeout (takeout.google.com).",
      "Select Keep only → Create export.",
      "Download the zip — each note becomes an individual .html or .txt file.",
      "Drop any .txt note here (headings like \"Ingredients\" and \"Method\" auto-detected).",
    ],
  },
  "import-from-obsidian": {
    label: "Obsidian / OneNote",
    method: "file",
    accept: ".md,.txt",
    tagline: "Turn your Obsidian, OneNote or generic markdown notes into structured RecipeFiler recipes.",
    keywords: "import obsidian recipes, onenote recipes, markdown recipes, .md recipe import",
    export_steps: [
      "Locate the .md file for your recipe (Obsidian vault or OneNote export).",
      "We handle bullet lists, numbered steps and headings automatically.",
      "Everything under an \"Ingredients\" heading is parsed as ingredients.",
      "Everything under a \"Method\" heading is parsed as steps.",
    ],
  },
  "import-from-eat-your-books": {
    label: "Eat Your Books",
    method: "url",
    tagline: "Cookbook indexer users — save the recipes you actually cook the most into RecipeFiler.",
    keywords: "import eat your books, eatyourbooks alternative, cookbook indexer",
    export_steps: [
      "Eat Your Books indexes your cookbooks; it doesn't export full recipes.",
      "For any online recipe URL you have bookmarked there, paste it into our URL importer.",
      "For your own private notes, use paste-text on the /scan page.",
      "Batch-paste up to 500 URLs at once on /import.",
    ],
  },
  // -------- URL-scrapable big brands --------
  "import-from-cookpad": {
    label: "Cookpad",
    method: "url",
    tagline: "Save every Cookpad recipe you love into a print-ready RecipeFiler cookbook.",
    keywords: "import cookpad recipes, cookpad to recipefiler, cookpad alternative, save cookpad recipes",
    export_steps: [
      "Open any Cookpad recipe page.",
      "Copy the URL.",
      "Paste it on our /try page (free) or bulk paste up to 500 on /import.",
      "We fetch schema.org data + rehost the hero photo.",
    ],
  },
  "import-from-yummly": {
    label: "Yummly",
    method: "url",
    tagline: "Bring your Yummly recipe box over — every URL rebuilds as a clean, editable RecipeFiler recipe.",
    keywords: "import yummly recipes, yummly alternative, save yummly recipes",
    export_steps: [
      "Open your Yummly recipe box.",
      "Copy each recipe URL (or a saved-recipe list URL).",
      "Paste up to 500 URLs on /import.",
      "Photos rehosted permanently so they never break.",
    ],
  },
  "import-from-hellofresh": {
    label: "HelloFresh",
    method: "url",
    tagline: "Turn your HelloFresh weekly recipe cards into a permanent cookbook you own.",
    keywords: "import hellofresh recipes, hellofresh recipe card, save hellofresh recipes",
    export_steps: [
      "Open the HelloFresh recipe page (or your Cookbook history).",
      "Copy the recipe URL.",
      "Paste it on /try (free, no sign-up) or up to 500 URLs on /import.",
      "We'll fetch the schema.org recipe + rehost the photo.",
    ],
  },
  "import-from-substack": {
    label: "Substack recipes",
    method: "url",
    tagline: "Rescue every recipe you love from your favourite cook's Substack — permanent, printable, portable.",
    keywords: "import substack recipes, save substack recipe, substack cookbook",
    export_steps: [
      "Open the Substack post with the recipe.",
      "Copy the post URL.",
      "Paste it on /try or bulk paste up to 500 on /import.",
      "If schema.org data is present we structure it; otherwise our AI reads the prose.",
    ],
  },
};


export default function ImportLanding() {
  const { slug: paramSlug } = useParams();
  const location = useLocation();
  const slug = paramSlug || location.pathname.replace(/^\//, "");
  const nav = useNavigate();
  const guides = useImportGuides();
  const content = LANDING[slug];

  if (!content) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center">
        <h1 className="text-2xl font-semibold">Import source not found</h1>
        <Link to="/import" className="text-rf-orange underline mt-4 inline-block">Back to Import →</Link>
      </div>
    );
  }

  const title = `Import from ${content.label} — RecipeFiler`;
  const url = `https://recipefiler.com/${slug}`;

  // Build the final action step programmatically so every landing page ends
  // with a crystal-clear "click X on this page to import" line — no more
  // vague "Drop it here" wording that leaves users hunting for the dropzone.
  const uploadHereStep =
    content.method === "screenshot"
      ? `Come back to this page and click "Open Scan" on the right (or below on mobile). Drop your screenshots one at a time — our OCR rebuilds each one into a fully editable recipe.`
      : content.method === "url"
      ? `Come back to this page and click "Paste URLs" on the right (or below on mobile). Paste your URLs in the textarea and hit "Fetch recipes".`
      : `Come back to this page and click "Open Upload dropzone" on the right (or below on mobile). Pick the file you just exported and we'll parse every recipe automatically.`;
  const guide = guides?.guides?.find((g) => g.key === GUIDE_FOR[slug] && g.key !== "screenshot");
  const displaySteps = guide && guide.method !== content.method ? guide.steps : [...(guide ? guide.steps : content.export_steps), uploadHereStep];

  const howToLd = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": `How to import ${content.label} recipes into RecipeFiler`,
    "description": content.tagline,
    "totalTime": "PT2M",
    "step": displaySteps.map((step, i) => ({
      "@type": "HowToStep",
      "position": i + 1,
      "text": step,
    })),
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <PageMeta
        title={title}
        description={content.tagline}
        canonical={url}
        keywords={content.keywords}
      />
      <BreadcrumbJsonLd items={[
        { name: "Home", url: "https://recipefiler.com/" },
        { name: "Import", url: "https://recipefiler.com/import" },
        { name: content.label, url },
      ]} />
      <script type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToLd) }} />

      <p className="text-sm text-rf-orange font-semibold uppercase tracking-wider">
        <Link to="/import" className="hover:underline">← Back to Import</Link>
      </p>
      <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-tight" data-testid="landing-h1">
        Import your recipes from {content.label}.
      </h1>
      <p className="mt-4 text-lg text-slate-600 max-w-3xl">{content.tagline}</p>
      {GUIDE_FOR[slug] && (
        <p className="mt-3 text-sm" data-testid="landing-import-guide">
          <Link to={slug === "import-from-evernote" ? "/help/evernote-import" : `/help/import/${GUIDE_FOR[slug]}`} className="text-rf-orange underline font-semibold">Read the full step-by-step {content.label} guide →</Link>
        </p>
      )}

      <div className="grid md:grid-cols-2 gap-8 mt-10">
        <div>
          <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
            <FileText size={22} className="text-rf-orange" /> How to import from {content.label}
          </h2>
          <ol className="space-y-3">
            {displaySteps.map((s, i) => {
              const isFinal = i === displaySteps.length - 1;
              return (
                <li
                  key={i}
                  data-testid={isFinal ? "landing-final-step" : undefined}
                  className={"flex items-start gap-3 rounded-lg " +
                    (isFinal ? "bg-rf-amber/10 border border-rf-amber/40 p-3 -ml-3" : "")}>
                  <span className={"w-7 h-7 rounded-full font-semibold flex items-center justify-center text-sm shrink-0 " +
                    (isFinal ? "bg-rf-orange text-white" : "bg-rf-amber/20 text-rf-orange")}>
                    {i + 1}
                  </span>
                  <span className="text-slate-700 pt-0.5">
                    {isFinal && <strong className="text-rf-orange">Final step: </strong>}{s}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
        <aside className="rounded-2xl border-2 border-rf-orange/30 bg-white p-6 md:sticky md:top-24 self-start"
               data-testid="landing-cta-card">
          <div className="inline-flex items-center gap-2 rounded-full bg-rf-amber/20 text-rf-orange px-3 py-1 text-xs font-bold uppercase tracking-wider">
            Step {displaySteps.length} · Import here
          </div>
          <h2 className="text-xl font-semibold mt-3 mb-3 flex items-center gap-2">
            <Sparkle size={22} className="text-rf-orange" weight="fill" /> What comes across
          </h2>
          <ul className="space-y-2 text-sm text-slate-700">
            {[
              "Recipe title, ingredients & method",
              "Servings, prep + cook time",
              "Photos (rehosted so they never break)",
              "Categories & tags",
              "Source URL for attribution",
              "Notes, difficulty, ratings where available",
            ].map((t) => (
              <li key={t} className="flex items-center gap-2">
                <CheckCircle size={18} weight="fill" className="text-emerald-600" /> {t}
              </li>
            ))}
          </ul>
          <button
            onClick={() => nav(content.method === "screenshot" ? "/scan" : `/import?tab=${content.method}`)}
            data-testid="landing-cta"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-rf-orange text-white font-semibold hover:bg-rf-orange/90 shadow-lg shadow-rf-orange/20">
            {content.method === "screenshot" ? (<><Camera size={18} /> Open Scan page →</>) :
             content.method === "url" ? (<><Globe size={18} /> Open Paste-URLs →</>) :
             (<><FileText size={18} /> Open Upload dropzone →</>)}
          </button>
          <p className="mt-3 text-center text-xs text-slate-500">
            {content.method === "file"
              ? "The Import page opens with the File tab pre-selected. Drop your file on the big amber dropzone."
              : content.method === "url"
              ? "The Import page opens with the URL tab pre-selected. Paste one URL per line, up to 500 at a time."
              : "The Scan page opens ready to accept screenshots. Photos, PDFs, or clipped images all work."}
          </p>
          <Link to="/try" className="mt-3 block text-center text-sm text-slate-600 hover:text-rf-orange">
            Just want to try one? Preview free (no sign-up) →
          </Link>
        </aside>
      </div>

      <FaqSection label={content.label} />

      {/* Cross-promote */}
      <section className="mt-16 rounded-2xl border border-amber-200 bg-amber-50/50 p-6 text-center">
        <p className="text-slate-700">
          Coming from another app? See our full{" "}
          <Link to="/import" className="text-rf-orange underline font-semibold">supported formats list →</Link>
        </p>
      </section>
    </div>
  );
}


function FaqSection({ label }) {
  const items = [
    {
      q: "Is the import free?",
      a: "Yes. Importing recipes is always free — even for anonymous visitors. You only need an account to save what you import to your permanent cookbook shelf.",
    },
    {
      q: `Will my ${label} photos come with me?`,
      a: "Yes. Where photos are embedded (Paprika, Mela, Recipe Keeper, Cookmate, Evernote) we extract them from the file. Where they're linked via URL, we fetch and rehost them to our own storage so they never break — even if the source site goes down.",
    },
    {
      q: "How does deduplication work?",
      a: "Every import compares title + first ingredient against your existing recipes in the target cookbook. Exact matches are skipped. We show you the skip count when the import finishes so you can spot anything unexpected.",
    },
    {
      q: "How big an import can I do?",
      a: "Files up to 500 MB each (photos included) and URL batches up to 500 at a time. Big files upload in pieces with a progress bar, and if your connection drops you can pick the same file again to carry on where it stopped. That's typically enough for a lifetime of home cooking. Need more? Contact us at contactus@recipefiler.com.",
    },
  ];
  // FAQPage JSON-LD — Google renders these as rich answer accordions
  // directly in search results. Cheap, huge SEO win per landing page.
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map((it) => ({
      "@type": "Question",
      "name": it.q,
      "acceptedAnswer": { "@type": "Answer", "text": it.a },
    })),
  };
  return (
    <section className="mt-14">
      <script type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />
      <h2 className="text-2xl font-display font-semibold mb-4">Frequently asked</h2>
      <div className="space-y-3">
        {items.map((it, i) => (
          <details key={i} className="rounded-xl border border-slate-200 bg-white p-4 group">
            <summary className="font-semibold cursor-pointer text-slate-800 group-open:text-rf-orange">
              {it.q}
            </summary>
            <p className="mt-3 text-slate-600 text-sm">{it.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
